import type { Locale, ShareMoment, ShareOffer } from '@kilat/shared';

/**
 * Words for a Share-kit moment, in the language the operator chose to post in
 * (Share & grow). Pure: a translate function in, strings out — so the test can
 * hand it the real catalogue and assert real Malay.
 *
 * Every value interpolated here came from the operator's own records via
 * `deriveShareMoments`; nothing is made up on the way to the page. The review
 * moment never names its author — the moment does not carry one.
 */

export type Translate = (key: string, vars?: Record<string, string | number>) => string;

export interface MomentCopy {
  /** The post text, before the link is attached. */
  readonly caption: string;
  /** Small line above the headline on the image. */
  readonly eyebrow: string;
  /** The big words on the image. */
  readonly headline: string;
  /** One supporting line on the image. */
  readonly body: string;
}

const K = 'dashboard.shareKit';

/** `RM 5` / `RM 5.50` — whole ringgit without a pointless `.00`. */
export function ringgit(sen: number): string {
  const rm = sen / 100;
  return `RM ${Number.isInteger(rm) ? String(rm) : rm.toFixed(2)}`;
}

export function offerPhrase(t: Translate, offer: ShareOffer): string {
  switch (offer.kind) {
    case 'pctOff':
      return t(`${K}.offer.pctOff`, { pct: offer.pct });
    case 'fixedOff':
      return t(`${K}.offer.fixedOff`, { amount: ringgit(offer.sen) });
    case 'newPrice':
      return t(`${K}.offer.newPrice`, { amount: ringgit(offer.sen) });
    case 'bonusPoints':
      return t(`${K}.offer.bonusPoints`, { points: offer.points });
    case 'freeExtra':
      return t(`${K}.offer.freeExtra`);
  }
}

/** BCP-47 tag for date formatting in a caption's language. */
export const LOCALE_TAG: Record<Locale, string> = {
  en: 'en-MY',
  ms: 'ms-MY',
  zh: 'zh-CN',
  ta: 'ta-MY',
};

/** A review quote short enough to sit on an image without becoming a wall of text. */
export const IMAGE_QUOTE_MAX = 140;

export function shortQuote(text: string): string {
  if (text.length <= IMAGE_QUOTE_MAX) return text;
  const cut = text.slice(0, IMAGE_QUOTE_MAX);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > IMAGE_QUOTE_MAX * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}

export function momentCopy(
  t: Translate,
  moment: ShareMoment,
  ctx: { readonly shop: string; readonly locale: Locale },
): MomentCopy {
  const shop = ctx.shop;
  switch (moment.kind) {
    case 'promotion': {
      const name = moment.name[ctx.locale] ?? moment.name.en;
      const offer = offerPhrase(t, moment.offer);
      const until = new Intl.DateTimeFormat(LOCALE_TAG[ctx.locale], {
        day: 'numeric',
        month: 'short',
      }).format(new Date(moment.endsAtMs));
      return {
        caption: t(`${K}.captions.promotion`, { shop, name, offer, until }),
        eyebrow: t(`${K}.image.promotionEyebrow`),
        headline: name,
        body: t(`${K}.image.promotionBody`, { offer, until }),
      };
    }
    case 'milestone':
      return {
        caption: t(`${K}.captions.milestone`, { shop, count: moment.washes.toLocaleString() }),
        eyebrow: t(`${K}.image.milestoneEyebrow`),
        headline: t(`${K}.image.milestoneHeadline`, { count: moment.washes.toLocaleString() }),
        body: t(`${K}.image.milestoneBody`),
      };
    case 'openNow':
      return moment.closesAt
        ? {
            caption: t(`${K}.captions.openNow`, { shop, time: moment.closesAt }),
            eyebrow: t(`${K}.image.openEyebrow`),
            headline: t(`${K}.image.openHeadline`, { time: moment.closesAt }),
            body: t(`${K}.image.openBody`),
          }
        : {
            caption: t(`${K}.captions.open24h`, { shop }),
            eyebrow: t(`${K}.image.openEyebrow`),
            headline: t(`${K}.image.open24hHeadline`),
            body: t(`${K}.image.openBody`),
          };
    case 'quietNow':
      return {
        caption: t(`${K}.captions.quietNow`, { shop }),
        eyebrow: t(`${K}.image.quietEyebrow`),
        headline: t(`${K}.image.quietHeadline`),
        body: t(`${K}.image.quietBody`),
      };
    case 'referral':
      return {
        caption: t(`${K}.captions.referral`, { shop }),
        eyebrow: t(`${K}.image.referralEyebrow`),
        headline: t(`${K}.image.referralHeadline`),
        body: t(`${K}.image.referralBody`),
      };
    case 'review':
      return {
        caption: t(`${K}.captions.review`, { shop, quote: moment.text }),
        eyebrow: '★★★★★',
        headline: `“${shortQuote(moment.text)}”`,
        body: t(`${K}.image.reviewBody`),
      };
  }
}
